/**
 * src/components/auth/SignInModal.tsx
 * --------------------------------------
 * Modal dialog containing the Sign In / Sign Up flow.
 *
 * Fully implemented this will:
 *   - Use Modal (src/components/ui/Modal.tsx) as the shell.
 *   - Toggle between Sign In form and SignUpForm via tab UI.
 *   - Sign In form:
 *       Email and password inputs with validation.
 *       "Forgot password?" link that triggers a password reset email.
 *       Submit button calls signIn() from src/api/auth.ts.
 *       On success: updates auth store, merges anon session if present,
 *       closes modal, triggers session list refresh.
 *   - SignUpForm: delegates to SignUpForm component.
 *   - Show inline error messages for invalid credentials.
 *   - Accessible: focus trap, labelled inputs, aria-describedby errors.
 *
 * Props:
 *   isOpen     — boolean
 *   onClose    — () => void
 *   defaultTab — 'sign_in' | 'sign_up' (default: 'sign_in')
 *
 * Key dependencies:
 *   src/api/auth.ts, src/stores/auth.ts,
 *   src/components/ui/Modal.tsx, src/components/ui/Button.tsx,
 *   src/components/ui/Input.tsx, src/components/auth/SignUpForm.tsx
 */
import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { SignUpForm } from './SignUpForm'
import { useAuth } from '@/hooks/useAuth'

interface SignInModalProps {
  isOpen: boolean
  onClose: () => void
  defaultTab?: 'sign_in' | 'sign_up'
}

export function SignInModal({
  isOpen,
  onClose,
  defaultTab = 'sign_in',
}: SignInModalProps) {
  const [tab, setTab] = useState<'sign_in' | 'sign_up'>(defaultTab)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { signIn } = useAuth()

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await signIn(email, password)
      onClose()
    } catch {
      setError('Invalid email or password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Welcome back">
      {/* Tab switcher */}
      <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
        {(['sign_in', 'sign_up'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 py-1.5 rounded-md text-sm font-medium transition-colors ${
              tab === t
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t === 'sign_in' ? 'Sign In' : 'Sign Up'}
          </button>
        ))}
      </div>

      {tab === 'sign_in' ? (
        <form onSubmit={handleSignIn} className="flex flex-col gap-4">
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />

          {error && (
            <p role="alert" className="text-xs text-red-500">
              {error}
            </p>
          )}

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? 'Signing in…' : 'Sign In'}
          </Button>
        </form>
      ) : (
        <SignUpForm onSuccess={onClose} />
      )}
    </Modal>
  )
}
